import { type IPageTitleTrailItem } from '@umodeapporg/ui/components/Navigation/PageTitleNavigationContext';
import { type ISidebarMenuItem } from '@umodeapporg/ui/components/Shell/SidebarMenu';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@/domain/permissions/hooks/usePermissions';
import { PORTAL_PATH } from '@/domain/portal/helpers/sessionAudience';
import { usePortalAcesso } from '@/domain/portal/hooks/usePortalAcesso';

import { INavigationEntry, NAVIGATION_FOOTER, NAVIGATION_GROUPS } from './navigationGroups';

/**
 * The one navigation tree: the sidebar renders it and PageTitle derives its icon and
 * breadcrumb from it, so a route added here gets both without any per-page wiring.
 *
 * WHAT is in the menu, in which group and in which order lives in `navigationGroups`. This
 * hook only turns that list into the shape the library wants, translating each label on the
 * way, so moving a screen between groups never touches a component.
 *
 * Factory staff see the screens their role in the account allows (the dashboard permissions):
 * a screen the role cannot open leaves the menu. A brand buyer only reaches the Portal, so their
 * menu is that one row, with no group heading above it. Until the API says who is signed in (and
 * what their role allows) the menu stays empty: drawing the full one first would flash screens
 * the person cannot open.
 */
export function useNavigationItems(): Array<ISidebarMenuItem> {
  const { t } = useTranslation('common');
  const { audience } = usePortalAcesso();
  const { ready, canViewRoute } = usePermissions();

  return useMemo<Array<ISidebarMenuItem>>(() => {
    const toItem = (item: INavigationEntry): ISidebarMenuItem => ({
      icon: item.icon,
      text: t(item.textKey),
      to: item.to,
      ...(item.end === true ? { end: true } : {}),
    });

    if (audience === 'brandBuyer') {
      return NAVIGATION_GROUPS.flatMap((grupo) => grupo.items)
        .filter((item) => item.to === PORTAL_PATH)
        .map(toItem);
    }
    if (audience !== 'staff' || !ready) {
      return [];
    }
    return NAVIGATION_GROUPS.flatMap((grupo) =>
      grupo.items
        .filter((item) => canViewRoute(item.to))
        .map((item) => ({
          ...toItem(item),
          ...(grupo.section === undefined ? {} : { section: grupo.section }),
        })),
    );
  }, [t, audience, ready, canViewRoute]);
}

/**
 * The manual is not one of the subjects of the product, it is how to use all of them: it
 * leaves the groups and sits at the foot of the sidebar, right above the sign out row.
 *
 * Only the factory gets the foot: Cadastros, Configurações and the manual all read factory data
 * a brand buyer cannot open. Cadastros and Configurações follow the role like the groups; the
 * manual has no ids on purpose and never leaves, since it is the page that explains the product.
 */
export function useFooterItems(): Array<ISidebarMenuItem> {
  const { t } = useTranslation('common');
  const { isStaff } = usePortalAcesso();
  const { ready, canViewRoute } = usePermissions();

  return useMemo<Array<ISidebarMenuItem>>(
    () =>
      isStaff && ready
        ? NAVIGATION_FOOTER.filter((item) => canViewRoute(item.to)).map((item) => ({
            icon: item.icon,
            text: t(item.textKey),
            to: item.to,
          }))
        : [],
    [t, isStaff, ready, canViewRoute],
  );
}

const matchesRoute = (item: ISidebarMenuItem, pathname: string): boolean => {
  if (item.end) {
    return pathname === item.to;
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
};

/** Menu path (root to leaf) whose deepest item owns the pathname; empty when the route is not in the menu. */
const findMenuTrail = (items: Array<ISidebarMenuItem>, pathname: string): Array<ISidebarMenuItem> => {
  let best: Array<ISidebarMenuItem> = [];
  for (const item of items) {
    const childTrail = item.children ? findMenuTrail(item.children, pathname) : [];
    if (childTrail.length > 0) {
      const candidate = [item, ...childTrail];
      if (candidate.length > best.length) {
        best = candidate;
      }
      continue;
    }
    if (matchesRoute(item, pathname) && (best.length === 0 || item.to.length > best[best.length - 1].to.length)) {
      best = [item];
    }
  }
  return best;
};

export function usePageTitleTrailResolver(items: Array<ISidebarMenuItem>) {
  return useCallback(
    (pathname: string): Array<IPageTitleTrailItem> =>
      findMenuTrail(items, pathname).map((item, index) => ({
        key: `${item.to}-${index}`,
        label: item.text,
        to: item.to,
        icon: item.icon,
      })),
    [items],
  );
}
